import { getPrompt, savePrompt } from '@/services/prompt/index';
export default {
  namespace: 'modal',
  state: {
    // 默认配置
    config: {
      confirm: {
        title: '确认操作',
        content: '您确定要执行此操作吗？',
        okText: 'OK',
        cancelText: 'Cancel',
      },
      delete: {
        title: '确认删除',
        content: '删除后无法恢复，您确定要删除吗？',
        okText: '删除',
        cancelText: '取消',
      },
      warning: {
        title: '警告',
        content: '此操作可能产生不可预期的结果，请谨慎操作！',
        okText: '继续',
        cancelText: '取消',
      },
      upgrade: {
        title: '功能升级说明',
        okText: '我已知晓',
        features: ['优化了数据查询性能', '增强了导出功能', '改进了用户交互体验'],
        upgradeMessage: '请及时体验新功能！',
      },
    },
    // 通知读取状态存储 (userId -> page -> noticeType -> { version, readAt })
    noticeReadStatus: {},
    // 当前活跃的modal实例
    activeModals: [],
    // API配置缓存
    apiConfigCache: {},
    // 提示信息
    prompt: {
      code: 0, // 默认成功
      lowerTime: 0, // 生效开始时间
      upperTime: 0, // 生效结束时间
      prompt: '', // 提示信息
    },
  },
  effects: {
    *getPrompt({ payload }, { call, put, select }) {
      try {
        const {
          code = -1,
          data = {
            code: 0, // 默认成功
            lowerTime: 0, // 生效开始时间
            upperTime: 0, // 生效结束时间
            prompt: '', // 提示信息
          },
        } = yield call(getPrompt, payload);
        if (code === 1) {
          yield put({
            type: 'update',
            payload: {
              prompt: data,
            },
          });
        }
      } catch (error) {
        console.error('获取提示信息失败:', error);
      }
    },
    *updatePrompt({ payload }, { call, put, select }) {
      try {
        const { code = -1, data = {} } = yield call(savePrompt, payload);
        if (code === 1) {
          yield put({
            type: 'update',
            payload: {
              promptCode: data.promptCode,
            },
          });
        }
      } catch (error) {
        console.error('更新提示信息失败:', error);
      }
    },
    // 获取modal配置
    *fetchModalConfig({ payload }, { call, put, select }) {
      const { apiUrl, type } = payload;
      if (!apiUrl) return {};

      try {
        // 检查缓存
        const cacheKey = `${apiUrl}_${type}`;
        const cachedConfig = yield select((state) => state.modal.apiConfigCache[cacheKey]);
        if (cachedConfig) {
          return cachedConfig;
        }

        const response = yield call(fetch, apiUrl);
        if (!response.ok) throw new Error('API调用失败');
        const data = yield response.json();
        const config = data[type] || {};

        // 缓存配置
        yield put({
          type: 'cacheApiConfig',
          payload: { cacheKey, config },
        });

        return config;
      } catch (error) {
        console.warn('获取配置失败，使用默认配置:', error);
        return {};
      }
    },

    // 标记通知为已读
    *markNoticeAsRead({ payload }, { put, select }) {
      const { userId, noticeType = 'upgrade', page = 'global', version = '1.0.0' } = payload;

      const noticeReadStatus = yield select((state) => state.modal.noticeReadStatus);
      const updatedStatus = { ...noticeReadStatus };

      // 确保数据结构存在
      if (!updatedStatus[userId]) updatedStatus[userId] = {};
      if (!updatedStatus[userId][page]) updatedStatus[userId][page] = {};

      // 记录已读状态
      updatedStatus[userId][page][noticeType] = {
        version,
        readAt: Date.now(),
      };

      yield put({
        type: 'updateNoticeReadStatus',
        payload: updatedStatus,
      });

      // 同步到localStorage
      try {
        const storageKey = 'modal_read_notices';
        localStorage.setItem(storageKey, JSON.stringify(updatedStatus));
      } catch (error) {
        console.error('保存通知状态失败:', error);
      }
    },

    // 从localStorage加载通知状态
    *loadNoticeStatus(_, { put }) {
      try {
        const storageKey = 'modal_read_notices';
        const storedStatus = localStorage.getItem(storageKey);
        if (storedStatus) {
          const parsedStatus = JSON.parse(storedStatus);
          yield put({
            type: 'updateNoticeReadStatus',
            payload: parsedStatus,
          });
        }
      } catch (error) {
        console.error('加载通知状态失败:', error);
      }
    },

    // 添加活跃modal
    *addActiveModal({ payload }, { put, select }) {
      const activeModals = yield select((state) => state.modal.activeModals);
      const newActiveModals = [...activeModals, payload];

      yield put({
        type: 'updateActiveModals',
        payload: newActiveModals,
      });
    },

    // 移除活跃modal
    *removeActiveModal({ payload }, { put, select }) {
      const activeModals = yield select((state) => state.modal.activeModals);
      const newActiveModals = activeModals.filter((modal) => modal.id !== payload.id);

      yield put({
        type: 'updateActiveModals',
        payload: newActiveModals,
      });
    },

    // 清除所有活跃modal
    *clearAllActiveModals(_, { put }) {
      yield put({
        type: 'updateActiveModals',
        payload: [],
      });
    },
  },
  reducers: {
    // 更新配置
    updateConfig(state, { payload }) {
      return {
        ...state,
        config: { ...state.config, ...payload },
      };
    },

    // 更新通知读取状态
    updateNoticeReadStatus(state, { payload }) {
      return {
        ...state,
        noticeReadStatus: payload,
      };
    },

    // 更新活跃modal列表
    updateActiveModals(state, { payload }) {
      return {
        ...state,
        activeModals: payload,
      };
    },

    // 缓存API配置
    cacheApiConfig(state, { payload }) {
      const { cacheKey, config } = payload;
      return {
        ...state,
        apiConfigCache: {
          ...state.apiConfigCache,
          [cacheKey]: config,
        },
      };
    },

    // 清除API配置缓存
    clearApiConfigCache(state) {
      return {
        ...state,
        apiConfigCache: {},
      };
    },

    // 通用更新方法
    update(state, { payload }) {
      return { ...state, ...payload };
    },
  },
};
